import type { GetTradesRow } from '@morfi/backend-sdk-v2/client';

import { resolveTradeSide } from '@/lib/chart/trade-side';
import type { LiveTrade } from './types';

const DEFAULT_MAX_TRADES_PER_MARKET = 32;
const EMPTY_TRADES: readonly LiveTrade[] = [];
const EMPTY_IDS: readonly string[] = [];
const EMPTY_SNAPSHOT: MarketFeedTradeSnapshot = {
  trades: EMPTY_TRADES,
  liveSinceMountIds: EMPTY_IDS,
};

type FeedTradeRow = Pick<
  GetTradesRow,
  'eventId' | 'market' | 'outcomeIndex' | 'type' | 'side' | 'usdcAmount'
>;
type MarketListener = () => void;

export interface MarketFeedTradeEvent {
  readonly market: string;
  readonly trade: LiveTrade;
}

export interface MarketFeedTradeSnapshot {
  readonly trades: readonly LiveTrade[];
  readonly liveSinceMountIds: readonly string[];
}

export interface MarketFeedTradeResourceUsage {
  readonly markets: number;
  readonly trades: number;
  readonly subscribers: number;
}

export interface MarketFeedTradeStore {
  readonly getSnapshot: (market: string) => MarketFeedTradeSnapshot;
  readonly subscribe: (market: string, listener: MarketListener) => () => void;
  readonly ingest: (events: readonly MarketFeedTradeEvent[]) => void;
  readonly retainMarkets: (markets: readonly string[]) => void;
  readonly getResourceUsage: () => MarketFeedTradeResourceUsage;
}

/**
 * Route-local presentation store for the market feed's transient trade bubbles.
 * Canonical trade history remains SDK-owned; this keeps only the bounded fields
 * the overlay renders and notifies subscribers for the affected market alone.
 */
export function createMarketFeedTradeStore(
  maxTradesPerMarket: number = DEFAULT_MAX_TRADES_PER_MARKET,
): MarketFeedTradeStore {
  if (!Number.isInteger(maxTradesPerMarket) || maxTradesPerMarket < 1) {
    throw new RangeError('maxTradesPerMarket must be a positive integer');
  }

  const snapshots = new Map<string, MarketFeedTradeSnapshot>();
  const listeners = new Map<string, Set<MarketListener>>();

  function getSnapshot(market: string): MarketFeedTradeSnapshot {
    return snapshots.get(market) ?? EMPTY_SNAPSHOT;
  }

  function subscribe(market: string, listener: MarketListener): () => void {
    let marketListeners = listeners.get(market);
    if (marketListeners === undefined) {
      marketListeners = new Set<MarketListener>();
      listeners.set(market, marketListeners);
    }
    marketListeners.add(listener);
    return (): void => {
      marketListeners.delete(listener);
      if (marketListeners.size === 0) listeners.delete(market);
    };
  }

  function notify(market: string): void {
    for (const listener of listeners.get(market) ?? []) listener();
  }

  function ingest(events: readonly MarketFeedTradeEvent[]): void {
    const updates = new Map<string, MarketFeedTradeSnapshot>();
    for (const event of events) {
      const previous = updates.get(event.market) ?? snapshots.get(event.market) ?? EMPTY_SNAPSHOT;
      if (previous.liveSinceMountIds.includes(event.trade.id)) continue;
      const trades = [...previous.trades, event.trade].slice(-maxTradesPerMarket);
      updates.set(event.market, {
        trades,
        liveSinceMountIds: trades.map((trade) => trade.id),
      });
    }
    for (const [market, snapshot] of updates) {
      snapshots.set(market, snapshot);
      notify(market);
    }
  }

  function retainMarkets(markets: readonly string[]): void {
    const retained = new Set(markets);
    for (const market of snapshots.keys()) {
      if (retained.has(market)) continue;
      snapshots.delete(market);
      notify(market);
    }
  }

  function getResourceUsage(): MarketFeedTradeResourceUsage {
    let trades = 0;
    for (const snapshot of snapshots.values()) trades += snapshot.trades.length;
    let subscribers = 0;
    for (const marketListeners of listeners.values()) subscribers += marketListeners.size;
    return { markets: snapshots.size, trades, subscribers };
  }

  return { getSnapshot, subscribe, ingest, retainMarkets, getResourceUsage };
}

/**
 * Project the SDK's post-snapshot provenance into the feed store. REST rows,
 * inactive markets, incomplete rows, and rows missing from the current page
 * are deliberately excluded; ordering follows the SDK arrival log.
 */
export function selectLiveMarketTradeEvents(
  rows: readonly FeedTradeRow[],
  liveSinceMountIds: readonly string[],
  activeMarkets: readonly string[],
): readonly MarketFeedTradeEvent[] {
  const active = new Set(activeMarkets);
  const rowsById = new Map(rows.map((row) => [row.eventId, row]));
  const events: MarketFeedTradeEvent[] = [];
  for (const id of liveSinceMountIds) {
    const row = rowsById.get(id);
    if (row === undefined || !active.has(row.market) || row.usdcAmount === undefined) continue;
    const side = resolveTradeSide(row);
    if (side === undefined) continue;
    events.push({
      market: row.market,
      trade: {
        id: row.eventId,
        outcomeIndex: row.outcomeIndex,
        side,
        amountD6: row.usdcAmount,
      },
    });
  }
  return events;
}
